import isEqual from "lodash/isEqual";
import { setCookie } from "@litres/utils";

import type {
  IApiResponse,
  ArtShortSerializer,
  IResponse,
  ArtDetailSerializer,
} from "@litres-libs/types";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import { rtkqApi } from "../../api/rtkqApi";
import { TagsType } from "../../constants";

import type { RootState } from "../../../store";
import { setCustomCookieValue } from "../../../toolkit/foundationUser/foundationUserSlice";
import { setHeader } from "../../../toolkit/browser/browserSlice";

import type { IUserArtsWithSubscriptionQueryArgs } from "../userArts.types";
import {
  patchArtAddToWishlist,
  patchArtRemoveFromWishlist,
  patchRemoveArtFromList,
} from "../utils";

export const wishlistApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getWishlistArts: build.query<
      IApiResponse<ArtShortSerializer[]>["payload"],
      IUserArtsWithSubscriptionQueryArgs
    >({
      query: ({ limit, art_groups, after, only_in_subscription_id }) => {
        return {
          url: "/foundation/api/wishlist/arts",
          params: {
            limit,
            art_groups,
            after,
            only_in_subscription_id,
          },
          headers: {
            "accept-version": "2",
          },
        };
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.MY_BOOKS as const,
                id,
              })),
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST, TagsType.MY_BOOKS],
      transformResponse: (response: IApiResponse<ArtShortSerializer[]>) => {
        return response.payload;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { after: _after, ...rest } = queryArgs;
        return `${endpointName}_${JSON.stringify(rest)}`;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.after) {
          // при ручной инвалидации кэша арты могут дублироваться, поэтому добавлена фильтрация по id
          const newData = newResponse.data.filter(
            item => !currentCache.data.find(current => current.id === item.id)
          );
          currentCache.data.push(...newData);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),
    /**
     * Добавить арт в отложенное
     */
    addToWishlist: build.mutation<IApiResponse<IResponse>, { artId: number }>({
      query({ artId }) {
        return {
          url: `/foundation/api/wishlist/arts/${artId}`,
          method: "PUT",
          headers: {
            "accept-version": "2",
          },
        };
      },
      invalidatesTags: (_res, _err, { artId: id }) => [
        { type: TagsType.CART, id },
        { type: TagsType.MY_BOOKS, id },
        { type: TagsType.ART, id },
        { type: TagsType.MY_BOOKS, id: "STATS" },
        TagsType.FOLDERS,
        TagsType.USER_MONETIZATION_DATA,
      ],
      async onQueryStarted({ artId }, { dispatch, getState, queryFulfilled }) {
        /* Все это нужно из-за того что бэкенд не умеет по сиду АНРЕГА отдавать признак отложенности для артов
         * поэтому мы храним список отложенных в куке, сеттим в стор и проставляем их в заголовки к каждому запросу
         * по которым бэк уже отдает этот признак */
        const state = getState() as RootState;
        const isUserAuthorized = !!state.foundationUser.id;

        /* "оптимистично" обновляем этот массив, чтобы UI поменялся сразу
         *  если запрос на мутацию упадет изменения откатятся */
        if (!isUserAuthorized) {
          addArtToWishlist(state, artId, dispatch);
        }

        const entries = rtkqApi.util
          .selectInvalidatedBy(state, [
            { type: TagsType.ART_SHORT_LIST, id: artId },
          ])
          .filter(data => data.endpointName !== "getWishlistArts");

        const artEntry = rtkqApi.util
          .selectInvalidatedBy(state, [{ type: TagsType.ART, id: artId }])
          .filter(data => data.endpointName === "getArtData");

        const patches = entries.map(entry =>
          patchArtAddToWishlist(artId, dispatch, entry)
        );
        patches.push(
          ...artEntry.map(entry =>
            dispatch(
              rtkqApi.util?.updateQueryData(
                //@ts-ignore
                entry.endpointName,
                entry.originalArgs,
                draftArt => {
                  Object.assign<
                    ArtDetailSerializer,
                    Partial<ArtDetailSerializer>
                  >(draftArt, {
                    is_liked: true,
                  });
                }
              )
            )
          )
        );
        try {
          await queryFulfilled;
        } catch (err) {
          console.log(err);
          patches.forEach(patch => {
            patch.undo();
          });
          if (!isUserAuthorized) {
            removeArtFromWishlist(state, artId, dispatch);
          }
        }
      },
    }),
    /**
     * Удалить арт из отложенного
     */
    removeFromWishlist: build.mutation<
      IApiResponse<IResponse>,
      { artId: number }
    >({
      query({ artId }) {
        return {
          url: `/foundation/api/wishlist/arts/${artId}`,
          method: "DELETE",
          headers: {
            "accept-version": "2",
          },
        };
      },
      invalidatesTags: (result, error, { artId: id }) => [
        { type: TagsType.CART, id },
        { type: TagsType.MY_BOOKS, id },
        { type: TagsType.ART, id },
        { type: TagsType.MY_BOOKS, id: "STATS" },
        TagsType.FOLDERS,
        TagsType.USER_MONETIZATION_DATA,
      ],
      async onQueryStarted({ artId }, { dispatch, getState, queryFulfilled }) {
        /* Все это нужно из-за того что бэкенд не умеет по сиду АНРЕГА отдавать признак отложенности для артов
         * поэтому мы храним список отложенных в куке, сеттим в стор и проставляем их в заголовки к каждому запросу
         * по которым бэк уже отдает этот признак */
        const state = getState() as RootState;
        const isUserAuthorized = !!state.foundationUser.id;

        /* "оптимистично" обновляем этот массив, чтобы UI поменялся сразу
         *  если запрос на мутацию упадет изменения откатятся */
        if (!isUserAuthorized) {
          removeArtFromWishlist(state, artId, dispatch);
        }

        const entries = rtkqApi.util
          .selectInvalidatedBy(state, [
            { type: TagsType.ART_SHORT_LIST, id: artId },
          ])
          .filter(data => data.endpointName !== "getWishlistArts");
        const wishlistEntry = rtkqApi.util
          .selectInvalidatedBy(state, [{ type: TagsType.MY_BOOKS, id: artId }])
          .filter(data => data.endpointName === "getWishlistArts");
        const artEntry = rtkqApi.util
          .selectInvalidatedBy(state, [{ type: TagsType.ART, id: artId }])
          .filter(data => data.endpointName === "getArtData");

        const patches = entries.map(entry =>
          patchArtRemoveFromWishlist(artId, dispatch, entry)
        );
        // удаление арта из кэша для списка отложенного
        patches.push(
          ...wishlistEntry.map(entry =>
            patchRemoveArtFromList(artId, dispatch, entry)
          )
        );
        patches.push(
          ...artEntry.map(entry =>
            dispatch(
              rtkqApi.util?.updateQueryData(
                //@ts-ignore
                entry.endpointName,
                entry.originalArgs,
                draftArt => {
                  Object.assign<
                    ArtDetailSerializer,
                    Partial<ArtDetailSerializer>
                  >(draftArt, {
                    is_liked: false,
                  });
                }
              )
            )
          )
        );
        try {
          await queryFulfilled;
        } catch (err) {
          console.log(err);
          patches.forEach(patch => {
            patch.undo();
          });

          if (!isUserAuthorized) {
            addArtToWishlist(state, artId, dispatch);
          }
        }
      },
    }),
    /**
     * Добавить арты в отложенное списком
     */
    addArtsListToWishlist: build.mutation<
      IApiResponse<IResponse>,
      { artIds: number[] }
    >({
      query({ artIds }) {
        return {
          url: `/foundation/api/wishlist/arts`,
          method: "POST",
          body: { art_ids: artIds },
          headers: {
            "accept-version": "2",
          },
        };
      },
      invalidatesTags: (result, error, { artIds }) => [
        ...artIds.map(id => ({ type: TagsType.CART, id })),
        ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
        TagsType.FOLDERS,
        TagsType.USER_MONETIZATION_DATA,
      ],
      async onQueryStarted({ artIds }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        /* "оптимистично" обновляем этот массив, чтобы UI поменялся сразу
         *  если запрос на мутацию упадет изменения откатятся */
        if (!state.foundationUser.id) {
          addArtsListToWishlist(state, artIds, dispatch);
        }

        const entries = rtkqApi.util
          .selectInvalidatedBy(state, [
            ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
          ])
          .filter(data => data.endpointName !== "getWishlistArts");

        const patches = entries.map(entry =>
          patchArtAddToWishlist(artIds, dispatch, entry)
        );
        try {
          await queryFulfilled;
        } catch (err) {
          console.log(err);
          patches.forEach(patch => {
            patch.undo();
          });
          if (!state.foundationUser.id) {
            removeArtsListFromWishlist(state, artIds, dispatch);
          }
        }
      },
    }),
  }),
});

const addArtsListToWishlist = (
  state: RootState,
  artIds: number[],
  dispatch: ThunkDispatch<any, any, AnyAction>
) => {
  const cookieLKS = state.foundationUser.LKS;
  const newLKS = [
    ...cookieLKS,
    ...artIds.map(a => String(a)).filter(a => !cookieLKS.includes(a)),
  ];
  const LKS = newLKS.join(",");
  setCookie("LKS_LAST", String(newLKS.length));
  setCookie("LKS", LKS);
  dispatch(setCustomCookieValue({ LKS: newLKS }));
  dispatch(setHeader(["Wishlist", LKS]));
};

const removeArtsListFromWishlist = (
  state: RootState,
  artIds: number[],
  dispatch: ThunkDispatch<any, any, AnyAction>
) => {
  const cookieLKS = state.foundationUser.LKS;
  const newLKS = cookieLKS.filter(id => !artIds.includes(Number(id)));
  const LKS = newLKS.join(",");
  setCookie("LKS_LAST", String(newLKS.length));
  setCookie("LKS", LKS);
  dispatch(setCustomCookieValue({ LKS: newLKS }));
  dispatch(setHeader(["Wishlist", LKS]));
};

const addArtToWishlist = (
  state: RootState,
  artId: number,
  dispatch: ThunkDispatch<any, any, AnyAction>
) => {
  const currentLKS = state.foundationUser.LKS;
  const newLKS = [...currentLKS, `${artId}`];
  const newLKSCookieValue = newLKS.join(",");
  setCookie("LKS_LAST", String(newLKS.length));
  setCookie("LKS", newLKSCookieValue);
  dispatch(setCustomCookieValue({ LKS: newLKS }));
  dispatch(setHeader(["Wishlist", newLKSCookieValue]));
};

const removeArtFromWishlist = (
  state: RootState,
  artId: number,
  dispatch: ThunkDispatch<any, any, AnyAction>
) => {
  const currentLKS = state.foundationUser.LKS;
  const newLKS = currentLKS.filter(id => artId !== Number(id));
  const newLKSCookieValue = newLKS.join(",");
  setCookie("LKS_LAST", String(newLKS.length));
  setCookie("LKS", newLKSCookieValue);
  dispatch(setCustomCookieValue({ LKS: newLKS }));
  dispatch(setHeader(["Wishlist", newLKSCookieValue]));
};
